import { SURVEY_TEMPLATE_TYPES } from "constants/global";
import { APIData } from "types/cms/frontendTypes";
import { API_METHOD } from "types/common";

export interface AnswerChoice {
  title: string;           // Required, maxLength 50
  value: string;           // Required
  order: number;
  readMapper?: string;
  writeMapper?: string;
  unselectMapper?: string;
  subChoices?: SubChoice[];
  // SM-19: structured mapper model (choice level). AUTO choices derive read/write/unselect from these.
  mapperSource?: MapperSource;
  hpMapperClauses?: HpMapperClause[];
  apMapperClauses?: ApMapperClause[];
}

// ---- SM-19: structured mapper model (mirrors the BE components / grammar spec) ----
export type MapperSource = 'AUTO' | 'MANUAL';
export type MapperRole = 'READ' | 'WRITE' | 'UNSELECT';

export interface HomeProfileQuestionLink {
  attributePath: string;
}

export interface ApplianceQuestionLink {
  applianceId: number;
  operator: 'C' | 'T' | 'S' | 'A';
  attributeKey?: string;
}

export interface HpMapperClause {
  role: MapperRole;
  attributePath: string;
  operator?: 'D' | 'C' | 'NONE';
  value?: string;
  rangeStart?: string;
  rangeEnd?: string;
  isNull?: boolean;
}

export interface ApMapperClause {
  role: MapperRole;
  applianceIds: number[];
  groupOperator?: 'AND' | 'OR';
  operator: 'C' | 'T' | 'S' | 'A';
  value?: string;
  rangeStart?: string;
  rangeEnd?: string;
  attributeKey?: string;
  secondaryKey?: string;
  secondaryValue?: string;
  negate?: boolean;
  isNull?: boolean;
}

// Question-level structured fields (present on the question request/read types).
export interface StructuredMapperQuestionFields {
  mapperSource?: MapperSource;
  homeProfileLinks?: HomeProfileQuestionLink[];
  applianceLinks?: ApplianceQuestionLink[];
}

// ---- SM-20: mapper grammar catalog (served by GET /survey/getMapperCatalog) ----
export interface MapperGrammarRules {
  profileCodes: string[]
  choicelessTypes: string[]
  metadataHidesChoices: boolean
  hp: {
    readOperators: { code: string; label: string; valueControl: string }[]
    writeHasOperator: boolean
  }
  ap: {
    operators: { code: string; label: string; valueControl: string; writeControl?: string }[]
    groupOperators: { code: string; separator: string; label: string }[]
    groupsReadOnly: boolean
    supportsCompound: boolean
  }
  range: { format: string; upperExclusive: boolean; openUpper: string; bareEquality: boolean; note: string }
}

export interface HomeProfileAttributeCatalog {
  attributeId: string
  label: string
  accountType: string[]
  values: { id: string; tag: string; name: string }[]
}

export interface ApplianceCatalog {
  applianceId: number
  name: string
  categoryId: string
  subCategoryId?: string
  fuelType?: string[]
}

export interface ApplianceCategoryCatalog {
  categoryId: string
  name?: string
  shortCode?: string
}

export interface ApplianceAttributeCatalog {
  applianceId: number | null
  attributeKey: string
  values: string[]
  expressions: string[]
  hasCompound: boolean
  isRegex: boolean
}

export interface MapperCatalog {
  profileCodes: string[]
  rules: MapperGrammarRules
  homeProfileAttributes: HomeProfileAttributeCatalog[]
  appliances: ApplianceCatalog[]
  applianceCategories: ApplianceCategoryCatalog[]
  applianceAttributes: ApplianceAttributeCatalog[]
}

export namespace GetMapperCatalog {
  export const method: API_METHOD = "GET";
  export const apiEndpoint = '/survey/getMapperCatalog'
  export type retval = MapperCatalog
  export type params = {}
}

// ---- SM-27: reverse parser behind the "convert to structured" affordance ----
// UNSUPPORTED means the parse did not survive the BE's byte-for-byte round trip, so the
// question must be left on its legacy strings rather than converted lossily.
export type ParseStatus = 'SUPPORTED' | 'UNSUPPORTED'

export interface ParsedChoiceMappers {
  hpMapperClauses: HpMapperClause[]
  apMapperClauses: ApMapperClause[]
}

export interface ParsedQuestionMappers {
  homeProfileLinks: HomeProfileQuestionLink[]
  applianceLinks: ApplianceQuestionLink[]
  /** Positionally aligned with the choices sent; null for a choice that didn't parse. */
  choices: (ParsedChoiceMappers | null)[]
  parseStatus: ParseStatus
}

export namespace ParseQuestionMappers {
  export const method: API_METHOD = "POST";
  export const apiEndpoint = '/survey/parseQuestionMappers'
  export type retval = ParsedQuestionMappers
  export type params = {
    stateMapper?: string
    choices?: { readMapper?: string; writeMapper?: string; unselectMapper?: string }[]
  }
}

export interface SubChoice {
  title: string;
  value: string;
  order: number;
  selectionType: 'NONE' | 'LIST';
  subChoiceValidationType: 'NONE' | 'ASC_ALLOWED_VALUES';
  allowedValues?: Record<string, any>; // JSON field
}

export type CreateSurveyQuestionRequestData = Omit<APIData<'api::survey-question.survey-question'>, 
  'documentId' | 'createdAt' | 'updatedAt' | 'publishedAt' | 'questionId' | 'utility'> & 
  { localizations: SurveyQuestionLocalization[] }

export interface SurveyQuestionLocalization {
  locale : string;
  title : string;
  info ?: string
  choices : LocalisedAnswerChoice[]
}

export type LocalisedAnswerChoice = Omit<AnswerChoice, 
    'order' | 'subChoices' | 'readMapper' | 'writeMapper' | 'unselectMapper' > & 
    { subChoices?: LocalisedSubChoice[]}

export type LocalisedSubChoice = Omit<SubChoice, 'order'>
  
export type SurveyChoices = Pick<APIData<'api::survey-question.survey-question'>, 'choices'>['choices']

export interface SurveyTemplateLocalization {
  locale : string
  templateQuestions : SurveyTemplateQuestion[]
}

export interface SurveyTemplateQuestion {
  questionUid : string
  title : string
}

export namespace GetQuestionBank {

  export const method: API_METHOD = "GET";

  export const apiEndpoint = '/survey/getQuestionBank'

  export type retval = {
    data : {
      surveyCategories : APIData<'api::survey-category.survey-category'>[]
      surveyQuestions : {
        data : {
          draft : APIData<"api::survey-question.survey-question">[]
          published : APIData<"api::survey-question.survey-question">[]
          modified : APIData<"api::survey-question.survey-question">[]
        }
        meta : {
          count : {
            draft : number
            published : number
            modified : number
          }
        }
      }
    }
  }

  export type params = { 
  }
}

export namespace UpdateQuestions {

  export const method: API_METHOD = "POST";

  export const apiEndpoint = '/survey/updateQuestions'

  export type params = {
    utilityDocumentId : string
    pilotId : string
    data : Partial<(APIData<'api::survey-question.survey-question'> & { localizations : SurveyQuestionLocalization[] })>[]
  }

  export type retval = {
    error ?: Record<string, string>[]
    questions : APIData<'api::survey-question.survey-question'>[]
  }
}


export namespace CreateSurveyTemplate {

  export const method: API_METHOD = "POST";

  export const apiEndpoint = '/survey/createTemplate'

  export type retval = APIData<'api::survey-template.survey-template'>

  export type params = {
    templateType : (typeof SURVEY_TEMPLATE_TYPES)[number]
    label : string
  }
}

export namespace GetSurveyTemplates {

  export const method: API_METHOD = "GET";

  export const apiEndpoint = '/survey/getTemplates'

  export type retval = {
    data : {
      draft : APIData<'api::survey-template.survey-template'>[]
      modified : APIData<'api::survey-template.survey-template'>[]
      published : APIData<'api::survey-template.survey-template'>[]
    }
  }

  export type params = {
  }
}

export namespace UpdateAllTemplates {

  export const method: API_METHOD = "POST";

  export const apiEndpoint = '/survey/updateAllTemplates'

  export type retval = {
    templates : APIData<'api::survey-template.survey-template'>[],
    error : Record<string, string>[]
  }

  export type params = {
    utilityDocumentId : string
    data : Partial<(APIData<'api::survey-template.survey-template'> & { localizations : SurveyTemplateLocalization[] })>[]
  }
}

export namespace PublishTemplates {

  export const method: API_METHOD = "POST";

  export const apiEndpoint = '/survey/publishTemplates'

  export type retval = APIData<'api::survey-template.survey-template'>[]

  export type params = {
    data : string[]
  }
}

export namespace UnpublishTemplates {

  export const method: API_METHOD = "POST";

  export const apiEndpoint = '/survey/unpublishTemplates'

  export type retval = APIData<'api::survey-template.survey-template'>[]

  export type params = {
    data : string[]
  }
}

export namespace DiscardAllDraftTemplates {

  export const method: API_METHOD = "POST";

  export const apiEndpoint = '/survey/discardAllDraftTemplates'

  export type retval = string[]

  export type params = {
    data : string[]
  }
}